import Step1 from "./Step1";
import Step2 from "./Step2";
import Step3 from "./Step3";
import Step4 from "./Step4";
import Step5 from "./Step5";
import Step6 from "./Step6";
import Step7 from "./Step7";
import Card from "components/card";
import StepsCard from "./StepsCard";
import axiosClient from "axios-client.js";
import { useEffect, useMemo, useState } from "react";
import WizardButtons from "./WizardButtons";
import ModalHorizon from "components/modal";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import Loading from "components/loading/Loading";
import ErrorMessage from "components/ErrorMessage";
import { activationStates } from "variables/activations";
import { ACTIVATION_TYPES, SOURCE_TYPES } from "utils/constants";

const Wizard = ({
    activationType,
    initialData,
    stepsConfig,
    licenseTypesConfig,
    purchaseSteps,
}) => {
    const navigate = useNavigate();
    const { code = null } = useParams();

    const [load, setLoad] = useState(false);
    const [message, setMessage] = useState({});
    const [state, setState] = useState({
        type: activationType,
        status: activationStates.PENDING_COMPLETION,
        step: 1,
        id: 0,
        code: null,
        data: initialData,
    });

    const [modal, setModal] = useState({
        active: false,
        title: null,
        description: null,
        btnVisible: false,
        onClickBtn: null,
        btnLabel: null,
        requireConfirm: false,
    });

    const [available, setAvailable] = useState(false);
    const [backupAvailable, setBackupAvailable] = useState(false);
    const [modalConfirmed, setModalConfirmed] = useState(false);

    const cluster = state.data.licenses.some(
        (x) => x.code === "cluster" && x.active
    );

    const source = state?.data?.source;

    const dynamicSteps = useMemo(() => {
        if (source === SOURCE_TYPES.RESELLER) {
            return stepsConfig.filter(
                ({ number }) => !purchaseSteps.includes(number)
            );
        }

        return stepsConfig;
    }, [state]);

    const steps = {
        1: Step1,
        2: Step2,
        3: Step3,
        4: Step4,
        5: Step5,
        6: Step6,
        7: Step7,
    };

    const StepComponent = steps[state.step];

    useEffect(() => {
        if (code === null) return;

        setLoad(true);
        setMessage({});

        axiosClient
            .get(`/activation/${code}`)
            .then(({ data }) => {
                if (
                    data.type !== activationType ||
                    (data.status !== activationStates.PENDING_COMPLETION &&
                        data.status !==
                            activationStates.WAITING_FOR_SERVER_CODE)
                ) {
                    navigate("/my/pending-activations", {
                        replace: true,
                    });
                    return;
                }

                setState({
                    type: data.type,
                    status: data.status,
                    step: data.step,
                    id: data.id,
                    code: data.code,
                    data: { ...data.data, country: "ITALY" },
                });
            })
            .catch((err) => {
                const response = err.response;

                if (response) {
                    setMessage({
                        text: response.data.message,
                        type: "ERROR",
                        errors: response.data.errors
                            ? response.data.errors
                            : {},
                    });
                }
            })
            .finally(() => {
                setLoad(false);
            });
    }, []);

    const validateServerCode = async (backup) => {
        let serverCode = backup ? "server_code_backup" : "server_code";
        let ip = backup ? "ip_server_backup" : "ip_server";
        let mac = backup ? "mac_addresses_backup" : "mac_addresses";
        let is_hw = backup ? "is_hw_backup" : "is_hw";

        setMessage({});
        if (backup) {
            setBackupAvailable(false);
        } else {
            setAvailable(false);
        }
        axiosClient
            .get("/activation/" + state.code + "/validate-server-code", {
                params: {
                    code: state.data[serverCode],
                },
            })
            .then(({ data }) => {
                if (backup) {
                    setBackupAvailable(data.available ? true : false);
                } else {
                    setAvailable(data.available ? true : false);
                }
                if (data.available === true) {
                    setState((state) => ({
                        ...state,
                        data: {
                            ...state.data,
                            [ip]: data.ip,
                            [mac]: {
                                eth0: "",
                                eth1: "",
                                eth2: "",
                                eth3: "",
                                eth4: "",
                                ...data.mac_addresses,
                            },
                            [is_hw]: data.is_hw,
                        },
                    }));
                } else {
                    setMessage({
                        text:
                            data.message +
                            (cluster
                                ? " macchina " + (backup ? "backup" : "master")
                                : ""),
                        type: "ERROR",
                    });
                    setState((state) => ({
                        ...state,
                        data: {
                            ...state.data,
                            [ip]: null,
                            [mac]: null,
                        },
                    }));
                    return false;
                }
            })
            .catch((err) => {
                const response = err.response;
                if (backup) {
                    setBackupAvailable(false);
                } else {
                    setAvailable(false);
                }

                if (response) {
                    setMessage({
                        text:
                            response.data.message +
                            (cluster
                                ? " macchina " + (backup ? "backup" : "master")
                                : ""),
                        type: "ERROR",
                        errors: response.data.errors
                            ? response.data.errors
                            : null,
                    });
                }
                return false;
            })
            .finally(() => {
                setLoad(false);
            });
    };

    const proceedStep1 = () => {
        setLoad(true);
        setMessage({});

        if (
            source === SOURCE_TYPES.RESELLER &&
            activationType === ACTIVATION_TYPES.CLOUD
        ) {
            axiosClient
                .put("/activation/" + state.code, state)
                .then(() => {
                    navigate(`/my/activation-summary/${state.code}`, {
                        replace: true,
                    });
                })
                .catch((err) => {
                    const response = err.response;

                    if (response) {
                        setMessage({
                            text: response.data.message,
                            type: "ERROR",
                            errors: response.data.errors
                                ? response.data.errors
                                : {},
                        });
                    }
                })
                .finally(() => {
                    setLoad(false);
                });
        } else if (state.id !== 0) {
            axiosClient
                .put(`/activation/${state.code}`, state)
                .then(() => {
                    nextStep();
                })
                .catch((err) => {
                    const response = err.response;

                    if (response) {
                        setMessage({
                            text: response.data.message,
                            type: "ERROR",
                            errors: response.data.errors
                                ? response.data.errors
                                : {},
                        });
                    }
                })
                .finally(() => {
                    setLoad(false);
                });
        } else {
            axiosClient
                .post("/activation", state)
                .then(({ data }) => {
                    setState((state) => ({
                        ...state,
                        id: data.id,
                        code: data.code,
                    }));
                    nextStep();
                })
                .catch((err) => {
                    const response = err.response;

                    if (response) {
                        setMessage({
                            text: response.data.message,
                            type: "ERROR",
                            errors: response.data.errors
                                ? response.data.errors
                                : {},
                        });
                    }
                })
                .finally(() => {
                    setLoad(false);
                });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (state.step === 1) {
            setModal({
                active: true,
                title: "Confermi che i dati inseriti sono corretti e riferiti al cliente finale?",
                requireConfirm: true,
                btnVisible: true,
                onClickBtn: () => {
                    closeModal();
                    proceedStep1();
                },
                btnLabel: "Confermo",
            });
            return;
        }

        if (state.step === 3) {
            if (state.data.total === 0) return false;

            if (
                state.data.licenses.some(
                    (x) => !x.active && x.qty && x.qty !== 0 && x.qty !== "0"
                )
            ) {
                setModal((previousState) => {
                    return {
                        ...previousState,
                        active: true,
                        title: "Licenze non confermate",
                        description:
                            "Alcune licenze sono state aggiunte ma non confermate per il carrello",
                        btnVisible: true,
                        onClickBtn: () => {
                            handleSubmit();
                        },
                        btnLabel: "Prosegui",
                    };
                });
                return false;
            }
        }

        setLoad(true);
        setMessage({});

        if (state.step === 3 && activationType === ACTIVATION_TYPES.CLOUD) {
            setMessage({});

            axiosClient
                .put("/activation/" + state.code, state)
                .then(() => {
                    navigate(`/my/activation-summary/${state.code}`, {
                        replace: true,
                    });
                })
                .catch((err) => {
                    const response = err.response;

                    if (response) {
                        setMessage({
                            text: response.data.message,
                            type: "ERROR",
                            errors: response.data.errors
                                ? response.data.errors
                                : {},
                        });
                    }
                })
                .finally(() => {
                    setLoad(false);
                });
        } else if (state.step === 5) {
            if (available === false) {
                setMessage({});
                setAvailable(false);

                axiosClient
                    .get("/activation/" + state.code + "/validate-fqdn", {
                        params: {
                            domain: state.data.fqdn,
                        },
                    })
                    .then(({ data }) => {
                        setAvailable(data.available ? true : false);
                        setMessage(
                            data.available === false
                                ? {
                                      text: data.message,
                                      type: "ERROR",
                                  }
                                : {}
                        );
                    })
                    .catch((err) => {
                        const response = err.response;
                        setAvailable(false);

                        if (response) {
                            setMessage({
                                text: response.data.message,
                                type: "ERROR",
                                errors: response.data.errors
                                    ? response.data.errors
                                    : null,
                            });
                        }
                    })
                    .finally(() => {
                        setLoad(false);
                    });
                return;
            }

            setLoad(true);
            setMessage({});

            axiosClient
                .put("/activation/" + state.code, state)
                .then(() => {
                    nextStep();
                })
                .catch((err) => {
                    const response = err.response;

                    if (response) {
                        setMessage({
                            text: response.data.message,
                            type: "ERROR",
                            errors: response.data.errors
                                ? response.data.errors
                                : {},
                        });
                    }
                })
                .finally(() => {
                    setLoad(false);
                });
        } else if (state.step === 7) {
            if (available === false || (backupAvailable === false && cluster)) {
                if (
                    state.data.server_code &&
                    state.data.server_code_backup &&
                    state.data.server_code === state.data.server_code_backup
                ) {
                    setMessage({
                        text: "errore: inserisci due codici server diversi",
                        type: "ERROR",
                        errors: "inserisci due codici server diversi",
                    });

                    setLoad(false);
                    return;
                }
                if (
                    state.data.server_code &&
                    (state.data.server_code_backup || !cluster)
                ) {
                    let result = await validateServerCode(false);
                    if (result !== false) {
                        if (cluster) {
                            validateServerCode(true);
                        }
                    }

                    setLoad(false);
                    return;
                }
                if (
                    state.data.server_code &&
                    cluster &&
                    !state.data.server_code_backup
                ) {
                    setMessage({
                        text: "errore: inserisci entrambi i codici server",
                        type: "ERROR",
                        errors: "inserisci entrambi i codici server",
                    });

                    setLoad(false);
                    return;
                }

                axiosClient
                    .put("/activation/" + state.code, state)
                    .then(() => {
                        navigate(`/my/activation-summary/${state.code}`, {
                            replace: true,
                        });
                    })
                    .catch((err) => {
                        const response = err.response;

                        if (response) {
                            setMessage({
                                text: response.data.message,
                                type: "ERROR",
                                errors: response.data.errors
                                    ? response.data.errors
                                    : {},
                            });
                        }
                    })
                    .finally(() => {
                        setLoad(false);
                    });
                return;
            }

            let addresses;
            let backupAddresses;

            if (state.data.server_code) {
                const checkMACFormatting = new RegExp(
                    /^([a-fA-F0-9]{2}[:|\-]?){6}$/
                );
                let quit = null;

                if (state?.data?.mac_addresses) {
                    addresses = state?.data?.mac_addresses;
                    Object.keys(addresses).forEach((key, index) => {
                        if (addresses[key] && addresses[key] !== "") {
                            if (checkMACFormatting.test(addresses[key])) {
                                addresses[key] = addresses[key].replaceAll(
                                    "-",
                                    ":"
                                );
                                addresses[key] = addresses[key].toLowerCase();
                            } else {
                                quit = key + (cluster ? " master" : "");
                            }
                        }
                    });
                }
                if (state?.data?.mac_addresses_backup) {
                    backupAddresses = state?.data?.mac_addresses_backup;
                    Object.keys(backupAddresses).forEach((key, index) => {
                        if (
                            backupAddresses[key] &&
                            backupAddresses[key] !== ""
                        ) {
                            if (checkMACFormatting.test(backupAddresses[key])) {
                                backupAddresses[key] = backupAddresses[
                                    key
                                ].replaceAll("-", ":");
                                backupAddresses[key] =
                                    backupAddresses[key].toLowerCase();
                            } else {
                                quit = key + (cluster ? " backup" : "");
                            }
                        }
                    });
                }

                if (quit) {
                    openInfoModal(
                        "Controlla formato indirizzi mac",
                        "L'indirizzo " +
                            quit +
                            " non è formattato correttamente"
                    );

                    setLoad(false);
                    return;
                } else {
                    setState((state) => {
                        state.data.mac_addresses = addresses;
                        return state;
                    });
                }
            }

            setLoad(true);
            setMessage({});
            axiosClient
                .put("/activation/" + state.code, {
                    ...state,
                    data: {
                        ...state.data,
                        mac_addresses: addresses,
                        mac_addresses_backup: backupAddresses,
                    },
                })
                .then(() => {
                    navigate(`/my/activation-summary/${state.code}`, {
                        replace: true,
                    });
                })
                .catch((err) => {
                    const response = err.response;

                    if (response) {
                        setMessage({
                            text: response.data.message,
                            type: "ERROR",
                            errors: response.data.errors
                                ? response.data.errors
                                : {},
                        });
                    }
                })
                .finally(() => {
                    setLoad(false);
                });
        } else {
            // other steps
            setAvailable(false);

            axiosClient
                .put(`/activation/${state.code}`, state)
                .then(() => {
                    nextStep();
                })
                .catch((err) => {
                    const response = err.response;

                    if (response) {
                        setMessage({
                            text: response.data.message,
                            type: "ERROR",
                            errors: response.data.errors
                                ? response.data.errors
                                : {},
                        });
                    }
                })
                .finally(() => {
                    setLoad(false);
                });
        }
    };

    function nextStep() {
        const currentState = state.step;
        const currentStepIdx = dynamicSteps.findIndex(
            ({ number }) => number === currentState
        );
        const nextStep = dynamicSteps[currentStepIdx + 1]?.number;

        setState((previousState) => {
            if (currentState === 3 && state.data.maintenance_skip === true) {
                return {
                    ...previousState,
                    step: currentState + 2,
                    maintenance: 0,
                    maintenance_data: {},
                };
            }

            return { ...previousState, step: nextStep };
        });
    }

    function prevStep() {
        if (state.step === 1) {
            if (state.id !== 0) return;

            navigate("/my/new-activation", {
                replace: true,
            });
        }

        const currentState = state.step;
        const currentStepIdx = dynamicSteps.findIndex(
            ({ number }) => number === currentState
        );
        const prevStep = dynamicSteps[currentStepIdx - 1]?.number;

        setState((previousState) => {
            if (currentState === 5 && state.data.maintenance_skip === true) {
                return {
                    ...previousState,
                    step: currentState - 2,
                    maintenance: 0,
                    maintenance_data: {},
                };
            }

            return { ...previousState, step: prevStep };
        });
    }

    function openInfoModal(title, description) {
        setModal({
            active: true,
            title: title,
            description: description,
        });
    }

    function closeModal() {
        setModalConfirmed(false);
        setModal((previousState) => {
            return {
                ...previousState,
                active: false,
                title: null,
                description: null,
                btnVisible: null,
                onClickBtn: null,
                btnLabel: null,
                requireConfirm: false,
            };
        });
    }

    return (
        <form onSubmit={handleSubmit}>
            {load && <Loading />}

            <ModalHorizon
                title={modal.title}
                description={modal.description}
                content={
                    modal.requireConfirm ? (
                        <div className="flex flex-col gap-6">
                            <div className="flex flex-col gap-4 text-sm text-gray-700">
                                <p>
                                    <strong>ATTENZIONE:</strong> prima di
                                    procedere con l&apos;invio, verifica che
                                    tutti i dati anagrafici inseriti siano
                                    corretti, aggiornati e riferiti al cliente
                                    finale a cui verranno associati i servizi.
                                </p>
                                <p>
                                    I campi relativi al{" "}
                                    <strong>Referente commerciale</strong> e al{" "}
                                    <strong>Referente tecnico</strong> possono
                                    invece essere compilati con i nominativi
                                    delle persone del tuo team che seguiranno la
                                    gestione commerciale e tecnica
                                    dell&apos;attivazione; non è necessario che
                                    corrispondano a persone del cliente finale.
                                </p>
                                <p>
                                    L&apos;inserimento di dati anagrafici non
                                    corretti, incompleti o non riconducibili al
                                    reale intestatario del servizio può
                                    compromettere la corretta attivazione e
                                    gestione della piattaforma.
                                </p>
                                <p>
                                    Il team di Delivery si riserva la facoltà di
                                    effettuare verifiche a campione sui dati
                                    inseriti e, ove necessario, di correggerli
                                    o richiederne la correzione, al fine di
                                    verificarne la correttezza e la veridicità.
                                </p>
                                <p>
                                    <strong>
                                        Confermi di voler procedere con
                                        l&apos;invio?
                                    </strong>
                                </p>
                            </div>
                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    id="wizard-confirm-step1"
                                    className="h-4 w-4 cursor-pointer accent-brand-500"
                                    checked={modalConfirmed}
                                    onChange={(e) =>
                                        setModalConfirmed(e.target.checked)
                                    }
                                />
                                <label
                                    htmlFor="wizard-confirm-step1"
                                    className="cursor-pointer select-none text-sm text-gray-700/90 transition hover:text-gray-800/90"
                                >
                                    Confermo la correttezza e la veridicità dei dati inseriti e autorizzo l’invio della richiesta.
                                </label>
                            </div>
                        </div>
                    ) : null
                }
                isOpen={modal.active}
                onClose={closeModal}
                btnVisible={modal.btnVisible}
                onClickBtn={modal.onClickBtn}
                btnLabel={modal.btnLabel}
                btnDisabled={modal.requireConfirm && !modalConfirmed}
            />

            <div className="flex flex-col gap-5 lg:flex-row">
                <div className="w-full lg:w-3/4">
                    <Card extra="!bg-white px-4 md:px-6 py-2 md:py-5 w-full h-full gap-8">
                        <div className="flex grow flex-col gap-8">
                            <StepComponent
                                activationType={activationType}
                                state={state}
                                setState={setState}
                                setLoad={setLoad}
                                message={message}
                                setMessage={setMessage}
                                licenseTypesConfig={licenseTypesConfig}
                                available={available}
                                backupAvailable={backupAvailable}
                                cluster={cluster}
                            />

                            <p className="text-[14.5px] text-gray-800 dark:text-white">
                                Clicca sul tasto “Avanti” per proseguire la
                                procedura di attivazione.
                            </p>

                            <ErrorMessage message={message} />
                        </div>

                        <WizardButtons
                            state={state}
                            prevStep={prevStep}
                            available={available}
                        />
                    </Card>
                </div>

                <div className="w-full lg:w-1/4">
                    <StepsCard state={state} stepsConfig={dynamicSteps} />
                </div>
            </div>
        </form>
    );
};

export default Wizard;
